import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Slider } from '../ui/slider';
import { 
  Save, 
  Eye, 
  Undo, 
  Redo,
  Move,
  Type,
  Image,
  Square,
  Circle,
  Download,
  Upload,
  Palette,
  Settings,
  Lock,
  Unlock,
  Crown,
  Globe,
  Shield,
  Plus,
  Trash2,
  Copy,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  MousePointer,
  Hand
} from 'lucide-react';

interface BaseTemplate {
  id: string;
  name: string;
  description: string;
  category: 'education' | 'professional' | 'government' | 'healthcare' | 'general';
  templateType: 'base' | 'organization' | 'custom';
  width: number;
  height: number;
  backgroundColor: string;
  backgroundImage?: string;
  fieldPositions: FieldPosition[];
  schemaRequirements: SchemaRequirement[];
  isGlobal: boolean;
  isMandatory: boolean;
  allowedOrganizations: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  usageCount: number;
  version: string;
  status: 'active' | 'draft' | 'deprecated';
}

interface FieldPosition {
  id: string;
  fieldName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  color: string;
  isRequired: boolean;
  isLocked: boolean;
}

interface SchemaRequirement {
  fieldName: string;
  fieldType: string;
  required: boolean;
  description: string;
}

interface BaseTemplateBuilderProps {
  template?: BaseTemplate | null;
  onSave: (template: BaseTemplate) => void;
  onCancel: () => void;
}

export default function BaseTemplateBuilder({ template, onSave, onCancel }: BaseTemplateBuilderProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [selectedTool, setSelectedTool] = useState<'select' | 'field' | 'text' | 'shape'>('select');
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState('design');
  const [showPreview, setShowPreview] = useState(false);

  const [templateData, setTemplateData] = useState<BaseTemplate>({
    id: template?.id || '',
    name: template?.name || '',
    description: template?.description || '',
    category: template?.category || 'general',
    templateType: 'base',
    width: template?.width || 800,
    height: template?.height || 600,
    backgroundColor: template?.backgroundColor || '#ffffff',
    backgroundImage: template?.backgroundImage,
    fieldPositions: template?.fieldPositions || [],
    schemaRequirements: template?.schemaRequirements || [],
    isGlobal: template?.isGlobal || true,
    isMandatory: template?.isMandatory || false,
    allowedOrganizations: template?.allowedOrganizations || ['*'],
    createdAt: template?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: template?.createdBy || 'super_admin',
    usageCount: template?.usageCount || 0,
    version: template?.version || '1.0.0',
    status: template?.status || 'draft'
  });

  const [canvasScale, setCanvasScale] = useState(1);
  const [undoStack, setUndoStack] = useState<BaseTemplate[]>([]);
  const [redoStack, setRedoStack] = useState<BaseTemplate[]>([]);

  const defaultFields = [
    { name: 'recipientName', label: 'Recipient Name', type: 'string', required: true },
    { name: 'issuingOrganization', label: 'Issuing Organization', type: 'string', required: true },
    { name: 'issueDate', label: 'Issue Date', type: 'date', required: true },
    { name: 'credentialTitle', label: 'Credential Title', type: 'string', required: true },
    { name: 'expirationDate', label: 'Expiration Date', type: 'date', required: false },
    { name: 'verificationUrl', label: 'Verification URL', type: 'url', required: true },
    { name: 'credentialId', label: 'Credential ID', type: 'string', required: true }
  ];

  // Save current state to undo stack
  const saveToUndoStack = () => {
    setUndoStack(prev => [...prev.slice(-19), { ...templateData }]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack(prev => [templateData, ...prev.slice(0, 19)]);
      setTemplateData(previousState);
      setUndoStack(prev => prev.slice(0, -1));
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[0];
      setUndoStack(prev => [...prev, templateData]);
      setTemplateData(nextState);
      setRedoStack(prev => prev.slice(1));
    }
  };

  const addField = (fieldName: string) => {
    saveToUndoStack();
    const newField: FieldPosition = {
      id: `field_${Date.now()}`,
      fieldName,
      x: 100,
      y: 100,
      width: 200,
      height: 30,
      fontSize: 16,
      fontWeight: 'normal',
      textAlign: 'left',
      color: '#1e293b',
      isRequired: defaultFields.find(f => f.name === fieldName)?.required || false,
      isLocked: false
    };

    setTemplateData(prev => ({
      ...prev,
      fieldPositions: [...prev.fieldPositions, newField]
    }));

    // Add to schema requirements if not exists
    const existingRequirement = templateData.schemaRequirements.find(r => r.fieldName === fieldName);
    if (!existingRequirement) {
      const defaultField = defaultFields.find(f => f.name === fieldName);
      if (defaultField) {
        setTemplateData(prev => ({
          ...prev,
          schemaRequirements: [...prev.schemaRequirements, {
            fieldName: defaultField.name,
            fieldType: defaultField.type,
            required: defaultField.required,
            description: `${defaultField.label} field`
          }]
        }));
      }
    }
  };

  const updateField = (fieldId: string, updates: Partial<FieldPosition>) => {
    saveToUndoStack();
    setTemplateData(prev => ({
      ...prev,
      fieldPositions: prev.fieldPositions.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
  };

  const deleteField = (fieldId: string) => {
    saveToUndoStack();
    setTemplateData(prev => ({
      ...prev,
      fieldPositions: prev.fieldPositions.filter(field => field.id !== fieldId)
    }));
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (selectedTool !== 'select') return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (e.clientX - rect.left) / canvasScale;
    const y = (e.clientY - rect.top) / canvasScale;

    // Find field at position
    const field = templateData.fieldPositions.find(f => 
      x >= f.x && x <= f.x + f.width && y >= f.y && y <= f.y + f.height
    );

    if (field) {
      setSelectedField(field.id);
      setIsDragging(true);
      setDragOffset({
        x: x - field.x,
        y: y - field.y
      });
    } else {
      setSelectedField(null);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !selectedField) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (e.clientX - rect.left) / canvasScale - dragOffset.x;
    const y = (e.clientY - rect.top) / canvasScale - dragOffset.y;

    updateField(selectedField, { x: Math.max(0, x), y: Math.max(0, y) });
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
  };

  const handleSave = () => {
    const savedTemplate: BaseTemplate = {
      ...templateData,
      id: templateData.id || `base_template_${Date.now()}`,
      updatedAt: new Date().toISOString(),
      status: 'active'
    };
    onSave(savedTemplate);
  };

  const selectedFieldData = selectedField 
    ? templateData.fieldPositions.find(f => f.id === selectedField)
    : null;

  if (showPreview) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-display font-bold text-charcoal-ink">
            Template Preview
          </h1>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Back to Editor
            </Button>
            <Button onClick={handleSave} className="credential-capital-button-primary">
              <Save className="h-4 w-4 mr-2" />
              Save Template
            </Button>
          </div>
        </div>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex justify-center">
              <div 
                className="relative border-2 border-gray-300 shadow-lg"
                style={{ 
                  width: templateData.width, 
                  height: templateData.height,
                  backgroundColor: templateData.backgroundColor,
                  backgroundImage: templateData.backgroundImage ? `url(${templateData.backgroundImage})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {templateData.fieldPositions.map(field => (
                  <div
                    key={field.id}
                    className="absolute border border-gray-400 bg-white/80 flex items-center justify-center text-center"
                    style={{
                      left: field.x,
                      top: field.y,
                      width: field.width,
                      height: field.height,
                      fontSize: field.fontSize,
                      fontWeight: field.fontWeight,
                      textAlign: field.textAlign,
                      color: field.color
                    }}
                  >
                    {field.fieldName}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-charcoal-ink">
            {template ? 'Edit' : 'Create'} Base Template
          </h1>
          <p className="text-warm-slate">
            Design a global template that organizations can inherit and customize
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={handleUndo} disabled={undoStack.length === 0}>
            <Undo className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={handleRedo} disabled={redoStack.length === 0}>
            <Redo className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="credential-capital-button-primary">
            <Save className="h-4 w-4 mr-2" />
            Save Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <div className="col-span-3 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="design">Design</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="design" className="space-y-4">
              {/* Tools */}
              <Card className="credential-capital-card">
                <CardHeader>
                  <CardTitle className="text-sm">Tools</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={selectedTool === 'select' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedTool('select')}
                      className="justify-start"
                    >
                      <MousePointer className="h-4 w-4 mr-2" />
                      Select
                    </Button>
                    <Button
                      variant={selectedTool === 'field' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedTool('field')}
                      className="justify-start"
                    >
                      <Type className="h-4 w-4 mr-2" />
                      Field
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Available Fields */}
              <Card className="credential-capital-card">
                <CardHeader>
                  <CardTitle className="text-sm">Available Fields</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {defaultFields.map(field => (
                    <Button
                      key={field.name}
                      variant="outline"
                      size="sm"
                      onClick={() => addField(field.name)}
                      className="w-full justify-start text-xs"
                    >
                      <Plus className="h-3 w-3 mr-2" />
                      {field.label}
                      {field.required && <Badge className="ml-auto text-xs">Required</Badge>}
                    </Button>
                  ))}
                </CardContent>
              </Card>

              {/* Field Properties */}
              {selectedFieldData && (
                <Card className="credential-capital-card">
                  <CardHeader>
                    <CardTitle className="text-sm">Field Properties</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">X</Label>
                        <Input
                          type="number"
                          value={selectedFieldData.x}
                          onChange={(e) => updateField(selectedField!, { x: parseInt(e.target.value) || 0 })}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Y</Label>
                        <Input
                          type="number"
                          value={selectedFieldData.y}
                          onChange={(e) => updateField(selectedField!, { y: parseInt(e.target.value) || 0 })}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Width</Label>
                        <Input
                          type="number"
                          value={selectedFieldData.width}
                          onChange={(e) => updateField(selectedField!, { width: parseInt(e.target.value) || 0 })}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Height</Label>
                        <Input
                          type="number"
                          value={selectedFieldData.height}
                          onChange={(e) => updateField(selectedField!, { height: parseInt(e.target.value) || 0 })}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Font Size</Label>
                      <Slider
                        value={[selectedFieldData.fontSize]}
                        onValueChange={([value]) => updateField(selectedField!, { fontSize: value })}
                        max={48}
                        min={8}
                        step={1}
                        className="mt-2"
                      />
                      <div className="text-xs text-warm-slate mt-1">{selectedFieldData.fontSize}px</div>
                    </div>

                    <div>
                      <Label className="text-xs">Text Align</Label>
                      <div className="flex space-x-1 mt-1">
                        <Button
                          variant={selectedFieldData.textAlign === 'left' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateField(selectedField!, { textAlign: 'left' })}
                          className="px-2"
                        >
                          <AlignLeft className="h-3 w-3" />
                        </Button>
                        <Button
                          variant={selectedFieldData.textAlign === 'center' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateField(selectedField!, { textAlign: 'center' })}
                          className="px-2"
                        >
                          <AlignCenter className="h-3 w-3" />
                        </Button>
                        <Button
                          variant={selectedFieldData.textAlign === 'right' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateField(selectedField!, { textAlign: 'right' })}
                          className="px-2"
                        >
                          <AlignRight className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Font Weight</Label>
                      <Select
                        value={selectedFieldData.fontWeight}
                        onValueChange={(value: 'normal' | 'bold') => updateField(selectedField!, { fontWeight: value })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="bold">Bold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">Text Color</Label>
                      <Input
                        type="color"
                        value={selectedFieldData.color}
                        onChange={(e) => updateField(selectedField!, { color: e.target.value })}
                        className="h-8 w-full mt-1"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={selectedFieldData.isLocked}
                        onCheckedChange={(checked) => updateField(selectedField!, { isLocked: checked })}
                      />
                      <Label className="text-xs">Lock Position</Label>
                      {selectedFieldData.isLocked ? (
                        <Lock className="h-3 w-3 text-amber-500" />
                      ) : (
                        <Unlock className="h-3 w-3 text-gray-400" />
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteField(selectedField!)}
                      className="w-full text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3 mr-2" />
                      Delete Field
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <Card className="credential-capital-card">
                <CardHeader>
                  <CardTitle className="text-sm">Template Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Template Name</Label>
                    <Input
                      value={templateData.name}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Global University Diploma"
                      className="h-8 text-xs mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Textarea
                      value={templateData.description}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the template purpose..."
                      className="text-xs mt-1"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Category</Label>
                    <Select
                      value={templateData.category}
                      onValueChange={(value: any) => setTemplateData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="h-8 text-xs mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card className="credential-capital-card">
                <CardHeader>
                  <CardTitle className="text-sm">Canvas Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Width</Label>
                      <Input
                        type="number"
                        value={templateData.width}
                        onChange={(e) => setTemplateData(prev => ({ ...prev, width: parseInt(e.target.value) || 800 }))}
                        className="h-8 text-xs mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Height</Label>
                      <Input
                        type="number"
                        value={templateData.height}
                        onChange={(e) => setTemplateData(prev => ({ ...prev, height: parseInt(e.target.value) || 600 }))}
                        className="h-8 text-xs mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Background Color</Label>
                    <Input
                      type="color"
                      value={templateData.backgroundColor}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="h-8 w-full mt-1"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="credential-capital-card">
                <CardHeader>
                  <CardTitle className="text-sm">Global Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={templateData.isGlobal}
                      onCheckedChange={(checked) => setTemplateData(prev => ({ ...prev, isGlobal: checked }))}
                    />
                    <Label className="text-xs">Global Template</Label>
                    <Globe className="h-3 w-3 text-blue-500" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={templateData.isMandatory}
                      onCheckedChange={(checked) => setTemplateData(prev => ({ ...prev, isMandatory: checked }))}
                    />
                    <Label className="text-xs">Mandatory Template</Label>
                    <Shield className="h-3 w-3 text-red-500" />
                  </div>
                  <div>
                    <Label className="text-xs">Version</Label>
                    <Input
                      value={templateData.version}
                      onChange={(e) => setTemplateData(prev => ({ ...prev, version: e.target.value }))}
                      placeholder="1.0.0"
                      className="h-8 text-xs mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Canvas Area */}
        <div className="col-span-9">
          <Card className="credential-capital-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Template Canvas</CardTitle>
                <div className="flex items-center space-x-2">
                  <Label className="text-xs">Zoom:</Label>
                  <Select
                    value={canvasScale.toString()}
                    onValueChange={(value) => setCanvasScale(parseFloat(value))}
                  >
                    <SelectTrigger className="w-20 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">50%</SelectItem>
                      <SelectItem value="0.75">75%</SelectItem>
                      <SelectItem value="1">100%</SelectItem>
                      <SelectItem value="1.25">125%</SelectItem>
                      <SelectItem value="1.5">150%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <div 
                  ref={canvasRef}
                  className="relative border-2 border-gray-300 cursor-crosshair overflow-hidden"
                  style={{ 
                    width: templateData.width * canvasScale, 
                    height: templateData.height * canvasScale,
                    backgroundColor: templateData.backgroundColor,
                    backgroundImage: templateData.backgroundImage ? `url(${templateData.backgroundImage})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                >
                  {templateData.fieldPositions.map(field => (
                    <div
                      key={field.id}
                      className={`absolute border-2 cursor-move flex items-center justify-center text-center select-none ${
                        selectedField === field.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-400 bg-white/80 hover:border-blue-400'
                      } ${field.isLocked ? 'border-amber-500' : ''}`}
                      style={{
                        left: field.x * canvasScale,
                        top: field.y * canvasScale,
                        width: field.width * canvasScale,
                        height: field.height * canvasScale,
                        fontSize: field.fontSize * canvasScale,
                        fontWeight: field.fontWeight,
                        textAlign: field.textAlign,
                        color: field.color
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedField(field.id);
                      }}
                    >
                      <span className="truncate px-1">
                        {field.fieldName}
                        {field.isLocked && <Lock className="inline h-3 w-3 ml-1 text-amber-500" />}
                      </span>
                    </div>
                  ))}
                  
                  {/* Grid overlay */}
                  <div 
                    className="absolute inset-0 pointer-events-none opacity-20"
                    style={{
                      backgroundImage: `
                        linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                        linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
                      `,
                      backgroundSize: `${20 * canvasScale}px ${20 * canvasScale}px`
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}